import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';

const WxCopilotLayoutContext = createContext();

// `isCopilotEnabled` is the single gate for whether the Cue panel can occupy
// layout space. It must match how WxCueRootHost decides to mount the panel
// surface. The raw `isPanelOpen` below is seeded from the shared
// `fountain_cue_panel` cookie (Path=/), which is set whenever the panel is
// opened on ANY Cue-enabled account in the browser. On an account without Cue
// the UMD never mounts, so nothing ever corrects that stale value — gating the
// EXPOSED state here keeps every consumer (page gutter, applicant drawer,
// Intercom) from reserving space for a panel that isn't there (HRAI-1989).
export function WxCopilotLayoutProvider({ children, isCopilotEnabled = false }) {
  // Read cookie at mount so isPanelOpen is correct before the UMD's Zustand store hydrates.
  const [isPanelOpen, setIsPanelOpen] = useState(
    () =>
      document.cookie.match(/(?:^|;\s*)fountain_cue_panel=([^;]*)/)?.[1] ===
      '1',
  );
  const [isWxCopilotReady, setIsWxCopilotReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = null;

    const tryAttach = () => {
      if (cancelled || unsubscribe) return !!unsubscribe;
      const api = window.WxCopilotComponent;
      if (!api?.subscribePanelState || !api.getIsPanelOpen) {
        return false;
      }
      // isOpen stays true in floating/pop-out — only clear layout push on close.
      // Sidebar vs floating is driven by the wx-copilot-panel event below.
      unsubscribe = api.subscribePanelState(next => {
        if (!next) setIsPanelOpen(false);
      });
      // Being subscribed is enough to be "ready": any future toggle corrects
      // isPanelOpen via the subscription, and the cookie already seeded the
      // initial value. Without this, the plain-poll success path would leave
      // readiness waiting on the :rendered event or the 2s fallback.
      setIsWxCopilotReady(true);
      return true;
    };

    const markReady = () => {
      if (cancelled) return;
      tryAttach();
      setIsWxCopilotReady(true);
    };

    // Push content only while docked as sidebar; floating overlays the page.
    // Always listen — subscribePanelState is boolean-only and misses mode changes.
    const handlePanelEvent = event => {
      const state = event.detail?.state;
      tryAttach();
      if (state != null) {
        setIsPanelOpen(state === 'sidebar');
      }
      setIsWxCopilotReady(true);
    };
    window.addEventListener('wx-copilot-panel', handlePanelEvent);

    const isAlreadyRendered =
      window.wxCopilotStateInternal?.rendered instanceof Map &&
      window.wxCopilotStateInternal.rendered.size > 0;

    if (isAlreadyRendered) {
      // SPA navigation: UMD store is already hydrated. Open-bit only — if the
      // panel is closed, drop layout push. If open, keep cookie seed (sidebar
      // default); floating is corrected when wx-copilot-panel fires on mode change.
      const api = window.WxCopilotComponent;
      if (api?.getIsPanelOpen && !api.getIsPanelOpen()) {
        setIsPanelOpen(false);
      }
      tryAttach();
      setIsWxCopilotReady(true);
      return () => {
        cancelled = true;
        window.removeEventListener('wx-copilot-panel', handlePanelEvent);
        if (unsubscribe) unsubscribe();
      };
    }

    const pollId = setInterval(() => {
      if (tryAttach()) {
        clearInterval(pollId);
      }
    }, 200);

    const onRendered = () => {
      markReady();
      clearInterval(pollId);
    };
    window.addEventListener('WxCopilotComponent:rendered', onRendered, {
      once: true,
    });

    // Safety net: never block Intercom forever if the UMD fails to load.
    const fallbackTimer = setTimeout(() => {
      if (!cancelled) {
        setIsWxCopilotReady(true);
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      clearTimeout(fallbackTimer);
      window.removeEventListener('wx-copilot-panel', handlePanelEvent);
      window.removeEventListener('WxCopilotComponent:rendered', onRendered);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Gate the exposed state (not just the cookie seed) so it self-corrects once
  // whoami resolves and every consumer inherits the corrected value.
  const effectiveIsPanelOpen = isCopilotEnabled && isPanelOpen;

  useEffect(() => {
    const offset = effectiveIsPanelOpen ? '400px' : '0px';
    const root = document.documentElement;
    root?.style?.setProperty('--wx-copilot-panel-offset', offset);
    return () => {
      root?.style?.removeProperty('--wx-copilot-panel-offset');
    };
  }, [effectiveIsPanelOpen]);

  const value = useMemo(
    () => ({
      isPanelOpen: effectiveIsPanelOpen,
      setIsPanelOpen,
      isWxCopilotReady,
    }),
    [effectiveIsPanelOpen, isWxCopilotReady],
  );

  return (
    <WxCopilotLayoutContext.Provider value={value}>
      {children}
    </WxCopilotLayoutContext.Provider>
  );
}

WxCopilotLayoutProvider.propTypes = {
  children: PropTypes.node.isRequired,
  isCopilotEnabled: PropTypes.bool,
};

// Connected variant used by the real app (app.js): reads whoami and derives the
// Cue-enabled gate. Kept separate from the bare provider above so unit tests
// (which wrap the bare provider via testUtils and widely mock
// `Auth_old/selectors`) never invoke the selector — only the real app does.
export function WxCopilotLayoutProviderConnected({ children }) {
  const whoami = useSelector(makeSelectWhoami());
  // Mirror WxCueRootHost's mount gate: the flag plus a provisioned wx account.
  const isCopilotEnabled = Boolean(whoami?.feature_flags?.['wx-copilot-enabled']);
  const accountWxExternalId = whoami?.account_wx_external_id;

  return (
    <WxCopilotLayoutProvider
      isCopilotEnabled={Boolean(isCopilotEnabled && accountWxExternalId)}
    >
      {children}
    </WxCopilotLayoutProvider>
  );
}

WxCopilotLayoutProviderConnected.propTypes = {
  children: PropTypes.node.isRequired,
};

const defaultValue = {
  isPanelOpen: false,
  setIsPanelOpen: () => {},
  isWxCopilotReady: false,
};

export function useWxCopilotLayout() {
  const context = useContext(WxCopilotLayoutContext);
  return context || defaultValue;
}
